import { forwardRef } from 'react'
import { useTranslation } from 'react-i18next'
import agency from '../../../api/agency/hooks'
import { Note } from '../../../api/notes/types'
import noPhoto from '../../../assets/images/no-photo.jpg'
import { formatDateTime } from '../../../utils/general'
import { Row, agentSection, detailsSection, locationSection, ownerSection, priceSection, profileSection, statusSection } from './sections'
import { AgentPrintData } from './types'

interface PrintContentProps {
    data: AgentPrintData
    notes: Note[]
    imageSrc: string | undefined
}

const PrintContent = forwardRef<HTMLDivElement, PrintContentProps>(({ data, notes, imageSrc }, ref) => {
    const { t } = useTranslation()
    
    const { data: agencyData } = agency.GetAgency.useQuery()

    const agent = data.agent
    const currentLang = localStorage.getItem('i18nextLng') || 'hr'
    const translation = data.translations?.[currentLang]

    const renderRows = (rows: Row[], bold = false) =>
        rows.map(({ label, value }) =>
            value ? (
                <div key={label} className="d-flex mb-1">
                    <span className={`${bold ? 'fw-bold ' : ''}me-2`}>{label}:</span>
                    <span>{value}</span>
                </div>
            ) : null
        )

    return (
        <main ref={ref}>
            <section>
                {/* Agency header */}
                <header className={`d-flex justify-content-${agencyData?.logo ? 'around' : 'end'} align-items-center bg-white mb-2 p-3`}>
                    {agencyData?.logo ? <img src={agencyData.logo} alt="Agency Logo" style={{ maxHeight: '100px', width: 'auto', objectFit: 'contain' }} /> : null}
                    <div className="fs-10">
                        <div>
                            {agencyData?.name}
                            {agencyData?.street ? `, ${agencyData.street} ${agencyData.streetNumber ?? ''}` : ''}
                        </div>
                        <div>
                            {agencyData?.zipCode} {agencyData?.city}, {agencyData?.country}
                            {agencyData?.phoneNumber ? `; tel: ${agencyData.phoneNumber}` : ''}
                        </div>
                        {agencyData?.email && <div>Email: {agencyData.email}</div>}
                        {agencyData?.websiteUrl && <div>Web: {agencyData.websiteUrl}</div>}
                    </div>
                </header>

                <div className="d-flex mx-2 gap-3">
                    {/* LEFT COLUMN */}
                    <div className="w-50 fs-12">
                        {/* Status */}
                        <div className="mb-2 p-2 border fs-12">{renderRows(statusSection(data, t), true)}</div>

                        {/* Profile */}
                        <div className="mb-2 p-2 border">{renderRows(profileSection(data, t), true)}</div>

                        {/* Location */}
                        <div className="mb-2 p-2 border">
                            <div className="fw-bold mb-1">{t('app.common.location')}:</div>
                            {renderRows(locationSection(data, t))}
                        </div>

                        {/* Price */}
                        <div className="mb-2 p-2 border">{renderRows(priceSection(data, t), true)}</div>

                        {/* Agent */}
                        {agent && (
                            <div className="mb-2 p-2 border">
                                <div className="fw-bold mb-1">{t('app.common.agent')}</div>
                                {renderRows(agentSection(agent, t))}
                            </div>
                        )}

                        {/* Owner */}
                        {data.owner.length > 0 && (
                            <div className="mb-2 p-2 border">
                                <div className="fw-bold mb-1">{t('app.common.owner')}</div>
                                {ownerSection(data.owner, t).map((ownerRows, i) => (
                                    <div key={i}>
                                        {i > 0 && <hr className="my-1" />}
                                        {renderRows(ownerRows)}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* RIGHT COLUMN */}
                    <div className="w-50 fs-12">
                        {/* Main photo */}
                        <div className="mb-2">
                            <img src={imageSrc ?? noPhoto} alt="Nekretnina" style={{ width: '100%', maxHeight: '220px', objectFit: 'cover' }} />
                        </div>

                        {/* Details */}
                        <div className="mb-2 p-2 border">
                            <div className="fw-bold mb-1">{t('app.common.otherInfo')}:</div>
                            {renderRows(detailsSection(data, t, translation))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Notes */}
            <div className="mb-2 p-2">
                <div className="fw-bold mb-1">{t('app.common.notes')}</div>
                {notes.length === 0 && <div className="text-muted">—</div>}
                {notes.map((note) => (
                    <div key={note.id} className="mb-2">
                        <div className="fw-semibold">{[formatDateTime(note.noteDate), note.noteTitle].filter(Boolean).join(' - ')}</div>
                        {note.note && <div>{note.note}</div>}
                    </div>
                ))}
            </div>

            {/* Description */}
            {translation?.propertyDescription && (
                <div className="p-2">
                    <div className="fw-bold mb-1">{t('app.common.description')}</div>
                    <div className="fs-12" dangerouslySetInnerHTML={{ __html: translation.propertyDescription }} />
                </div>
            )}
        </main>
    )
})

export default PrintContent
